/**
 * HELP CENTER GROUPS CONFIGURATION
 *
 * Display metadata for the `/podrska` help center's category groups. This is
 * a curation config, not a content source: `content/help/clients/*.md` and
 * `content/help/therapists/*.md` own the actual articles and their
 * `category` frontmatter, this file only owns how a `(audience, category)`
 * pair is presented, what order it renders in, and which
 * `TherapyGuideAccent` it borrows from `vodic-kroz-psihoterapiju.ts`.
 *
 * Lookup is keyed by `(audience, category)` rather than category alone
 * because "Osnove" exists as a category on both the client and therapist
 * side with unrelated meanings, and must resolve to two different groups.
 *
 * The six client groups map onto the five `THERAPY_GUIDE_ACCENTS`; the sixth
 * group ("AI alati") reuses the teal `osnove-psihoterapije` accent rather
 * than introducing a colour outside the brand palette.
 */

import {
  CalendarClock,
  Clock,
  CreditCard,
  Lock,
  type LucideIcon,
  MessagesSquare,
  Compass,
  ShieldCheck,
  Sparkles,
  UserSearch,
  Wallet,
} from "lucide-react";
import {
  getTherapyGuideAccent,
  type TherapyGuideAccent,
} from "~/config/vodic-kroz-psihoterapiju";
import type { HelpAudience } from "~/lib/help-center-search";

export interface HelpGroupDefinition {
  /** Matches `HelpArticle["audience"]` */
  audience: HelpAudience;
  /** Matches the article's `category` frontmatter, verbatim */
  category: string;
  /** Display title for the group's section heading */
  title: string;
  /** One-line description shown under the title */
  description: string;
  icon: LucideIcon;
  /** Key into `THERAPY_GUIDE_ACCENTS` */
  accentId: string;
  /** Render order within the audience, lower first */
  order: number;
}

export const HELP_CENTER_GROUPS: readonly HelpGroupDefinition[] = [
  // Client groups, in spec order
  {
    audience: "client",
    category: "Osnove i poverenje",
    title: "Osnove i poverenje",
    description:
      "Šta je platforma, kome je namenjena i zašto joj možeš verovati.",
    icon: ShieldCheck,
    accentId: "osnove-psihoterapije",
    order: 1,
  },
  {
    audience: "client",
    category: "Izbor terapeuta i prva sesija",
    title: "Izbor terapeuta i prva sesija",
    description: "Kako biraš terapeuta i šta te čeka na prvom razgovoru.",
    icon: UserSearch,
    accentId: "kome-se-obracas",
    order: 2,
  },
  {
    audience: "client",
    category: "Zakazivanje i sesije",
    title: "Zakazivanje i sesije",
    description:
      "Kako zakazuješ termin i šta se dešava tokom i posle sesije.",
    icon: CalendarClock,
    accentId: "sa-cime-terapija-pomaze",
    order: 3,
  },
  {
    audience: "client",
    category: "Cene i plaćanje",
    title: "Cene i plaćanje",
    description: "Koliko košta terapija, kako plaćaš i kako radi povraćaj novca.",
    icon: CreditCard,
    accentId: "cena-i-trajanje",
    order: 4,
  },
  {
    audience: "client",
    category: "Privatnost i nalog",
    title: "Privatnost i nalog",
    description: "Kako čuvamo tvoje podatke i kako podešavaš nalog.",
    icon: Lock,
    accentId: "terapijski-pravci",
    order: 5,
  },
  {
    audience: "client",
    category: "AI alati",
    title: "AI alati",
    description: "Kako ti AI asistent i testovi pomažu pre i posle sesije.",
    icon: Sparkles,
    accentId: "osnove-psihoterapije",
    order: 6,
  },
  // Therapist groups: existing categories, kept verbatim, given the same
  // display metadata shape purely so `/podrska/terapeuti` can render
  // through the identical code path.
  {
    audience: "therapist",
    category: "Osnove",
    title: "Osnove",
    description: "Prijava, dashboard i osnovna navigacija kroz platformu.",
    icon: Compass,
    accentId: "osnove-psihoterapije",
    order: 1,
  },
  {
    audience: "therapist",
    category: "Radno vreme",
    title: "Radno vreme",
    description: "Profil, radno vreme, vremenska zona i Google kalendar.",
    icon: Clock,
    accentId: "cena-i-trajanje",
    order: 2,
  },
  {
    audience: "therapist",
    category: "Rad sa klijentima",
    title: "Rad sa klijentima",
    description: "Razgovori, kalendar i video sesije sa klijentima.",
    icon: MessagesSquare,
    accentId: "sa-cime-terapija-pomaze",
    order: 3,
  },
  {
    audience: "therapist",
    category: "Finansije",
    title: "Finansije",
    description: "Cene, isplate i dokumentacija koja ti je potrebna.",
    icon: Wallet,
    accentId: "terapijski-pravci",
    order: 4,
  },
];

function groupKey(audience: HelpAudience, category: string): string {
  return `${audience}:${category}`;
}

const HELP_CENTER_GROUPS_BY_KEY: ReadonlyMap<string, HelpGroupDefinition> =
  new Map(
    HELP_CENTER_GROUPS.map((group) => [
      groupKey(group.audience, group.category),
      group,
    ]),
  );

export function getHelpGroupDefinition(
  audience: HelpAudience,
  category: string,
): HelpGroupDefinition | undefined {
  return HELP_CENTER_GROUPS_BY_KEY.get(groupKey(audience, category));
}

/**
 * Stable anchor id for a group's section, shared between the audience
 * page's in-page navigation (rendered in `PublicPageHeader`'s `afterTitle`)
 * and the section it points at, so both sides agree without re-deriving a
 * slug from display text independently.
 */
export function getHelpGroupAnchorId(definition: HelpGroupDefinition): string {
  return `${definition.audience}-${definition.category}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export interface ResolvedHelpGroup<TArticle> {
  definition: HelpGroupDefinition;
  accent: TherapyGuideAccent;
  articles: TArticle[];
}

/**
 * Resolves an audience and a flat list of articles into ordered, non-empty
 * groups. An article whose category has no matching `HelpGroupDefinition`
 * for its audience is dropped rather than silently grouped elsewhere; the
 * help-center test suite asserts this never happens for published content.
 */
export function resolveHelpGroups<
  TArticle extends { audience: HelpAudience; category: string },
>(
  audience: HelpAudience,
  articles: readonly TArticle[],
): ResolvedHelpGroup<TArticle>[] {
  const articlesForAudience = articles.filter(
    (article) => article.audience === audience,
  );

  return HELP_CENTER_GROUPS.filter((group) => group.audience === audience)
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((definition) => ({
      definition,
      accent: getTherapyGuideAccent(definition.accentId),
      articles: articlesForAudience.filter(
        (article) => article.category === definition.category,
      ),
    }))
    .filter((group) => group.articles.length > 0);
}
